import { useAppDispatch } from '@/app/reduxHooks'
import { initializePipelineDataFlow } from '@/features/adp/api/pipelineBootstrap.api'
import { setPipelineState, setStatus } from '@/features/adp/state/pipelineSlice'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

export const INITIALIZE_PIPELINE_QUERY_KEY = ['adp', 'pipeline-bootstrap'] as const

export function useInitializePipelineDataFlow() {
  const dispatch = useAppDispatch()
  const lastHandledErrorAtRef = useRef<number>(0)

  const query = useQuery({
    queryKey: INITIALIZE_PIPELINE_QUERY_KEY,
    queryFn: initializePipelineDataFlow,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
  })

  useEffect(() => {
    if (!query.isFetching) return
    dispatch(setStatus('initializing'))
  }, [dispatch, query.isFetching])

  useEffect(() => {
    if (!query.isSuccess || !query.data) return

    dispatch(
      setPipelineState({
        dataFlowId: query.data.data_flow_id,
        sessionId: query.data.session_id,
        pipelineName: query.data.data_flow_name || 'Untitled_Pipeline',
        pipelineDescription: query.data.description || '',
      })
    )
    dispatch(setStatus('idle'))
  }, [dispatch, query.data, query.isSuccess])

  useEffect(() => {
    if (!query.isError || !query.error) return
    if (lastHandledErrorAtRef.current === query.errorUpdatedAt) return
    lastHandledErrorAtRef.current = query.errorUpdatedAt

    toast.error(query.error.message || 'Failed to initialize pipeline.')
    dispatch(setStatus('idle'))
  }, [dispatch, query.error, query.errorUpdatedAt, query.isError])

  return query
}
